import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, Mail, Bell } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import JsonLD, { buildBreadcrumb } from "../components/JsonLD";
import { useLanguage } from "../context/LanguageContext";
import { SITE_NAME, SITE_URL, CONTACT_EMAIL } from "../translations";

/**
 * Generic "Coming Soon" category landing page used for /excel-tools, /word-tools, /image-tools.
 */
export default function ComingSoonCategory({ category }) {
  const { t, lang } = useLanguage();
  const location = useLocation();

  const copy = {
    en: {
      excel: { h1: "Excel Tools — Coming Soon", desc: "Convert, merge and clean up spreadsheets — Excel utilities are coming next on FileNesto.", roadmap: ["Excel to PDF", "PDF to Excel (already live)", "Merge XLSX files", "Excel data cleaner"] },
      word: { h1: "Word Tools — Coming Soon", desc: "Word document conversion and editing — Word utilities are coming next on FileNesto.", roadmap: ["Word to PDF (already live)", "PDF to Word (already live)", "Merge DOCX files", "Word document cleanup"] },
      image: { h1: "Image Tools — Coming Soon", desc: "Convert, compress and transform images — image utilities are coming next on FileNesto.", roadmap: ["Image to PDF", "Compress JPG / PNG", "Resize images", "Convert HEIC to JPG"] },
      back: "Browse PDF Tools (live today)",
      notify: "Want to be notified when this category launches? Email us:",
    },
    fr: {
      excel: { h1: "Outils Excel — Bientôt disponible", desc: "Conversion, fusion et nettoyage de tableurs — les outils Excel arrivent bientôt sur FileNesto.", roadmap: ["Excel vers PDF", "PDF vers Excel (déjà disponible)", "Fusionner des fichiers XLSX", "Nettoyage de données Excel"] },
      word: { h1: "Outils Word — Bientôt disponible", desc: "Conversion et édition de documents Word — les outils Word arrivent bientôt sur FileNesto.", roadmap: ["Word vers PDF (déjà disponible)", "PDF vers Word (déjà disponible)", "Fusionner des fichiers DOCX", "Nettoyage de documents Word"] },
      image: { h1: "Outils Image — Bientôt disponible", desc: "Conversion, compression et transformation d'images — les outils image arrivent bientôt sur FileNesto.", roadmap: ["Image vers PDF", "Compresser JPG / PNG", "Redimensionner des images", "Convertir HEIC en JPG"] },
      back: "Voir les outils PDF (disponibles aujourd'hui)",
      notify: "Vous souhaitez être informé du lancement ? Écrivez-nous :",
    },
  };

  const local = copy[lang] || copy.en;
  const c = local[category];
  const title = `${c.h1} — ${SITE_NAME}`;

  useEffect(() => {
    document.title = title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", c.desc);
    // noindex — coming soon pages must not be indexed until the category is live
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, follow");
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `${SITE_URL}${location.pathname}`;
    return () => {
      if (robots && document.head.contains(robots)) document.head.removeChild(robots);
    };
  }, [title, c.desc, location.pathname]);

  return (
    <div className="min-h-screen bg-white page-fade-in" data-testid={`page-${category}-tools`}>
      <JsonLD data={buildBreadcrumb(c.h1, location.pathname)} />
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors">
            <ChevronLeft className="w-4 h-4" />{t.nav.home}
          </Link>
        </div>

        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold uppercase tracking-wider text-slate-600 bg-slate-100 px-3 py-1 rounded-full mb-4">
            <Bell className="w-3 h-3 inline mr-1" />
            {t.categories.soonBadge}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {c.h1}
          </h1>
          <p className="text-slate-500 text-base max-w-xl mx-auto">{c.desc}</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-6 mb-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-3">
            {lang === "fr" ? "Sur la feuille de route" : "On the roadmap"}
          </h2>
          <ul className="space-y-2">
            {c.roadmap.map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-sm text-slate-700">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-6 mb-6 text-center">
          <p className="text-sm text-slate-700 mb-3">{local.notify}</p>
          <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors" data-testid="coming-soon-email">
            <Mail className="w-4 h-4" /> {CONTACT_EMAIL}
          </a>
        </div>

        <div className="text-center">
          <Link to="/pdf-tools" className="inline-flex items-center gap-2 text-blue-600 font-medium hover:gap-3 transition-all" data-testid="back-to-pdf-tools">
            {local.back} →
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
